// src/lib/confirmationData.js
//
// Wave 3b (part 1) — NEW, ADDITIVE FILE. Replacement data+Realtime layer for
// every direct Firestore read/write/onSnapshot call in ClientDashboard.js /
// KnowledgeBasePage.js / BostaPage.js / WebhookSetupPage.js. Not imported by
// any of those files yet — zero behavior change today. Full file:line
// mapping from old call site -> function in this file lives in
// src/lib/MIGRATION_MAP.md; read that alongside this file.
//
// ============================================================================
// DESIGN NOTES (read before wiring this into any component)
// ============================================================================
// 1. Identity is NEVER re-derived inside these functions. Every function that
//    needs "which brand" takes a `brand` (the brand's `doc_id`/`brand_name` —
//    they are the same string for every brand row seen live, see
//    daf_confirmation_schema.sql) as an explicit parameter, resolved exactly
//    once upstream via `getMyBrand()` from ./supabaseAuth.js (which itself
//    resolves from the GoTrue session, never from a URL param — see that
//    file's header comment for the uid-in-URL fix this whole Wave is about).
//    This mirrors how the OLD code already worked (every ClientDashboard.js
//    query below takes `clientInfo.brand_name` as an input, not a URL param)
//    — the only thing that changes is *how* `clientInfo`/`brand` was
//    obtained in the first place.
//
// 2. Every `subscribeX(brand, params, cb)` below does two things, per the
//    task brief:
//      (a) an initial fetch matching the OLD Firestore query's filter/order/
//          limit exactly (documented per-function below with the source
//          file:line), calling `cb({rows, error: null})` (or
//          `cb({rows: [], error})` on failure) immediately;
//      (b) opens a `supabase.channel(...).on('postgres_changes', ...)`
//          subscription and, on ANY insert/update/delete event, RE-RUNS the
//          exact same fetch and calls `cb(...)` again with the fresh result.
//    Re-running the full fetch (instead of patching individual rows from the
//    change payload) is a deliberate choice, not a shortcut: Supabase
//    Realtime's `postgres_changes` filter syntax only supports a single flat
//    `column=eq.value` predicate — it cannot express "brand_name = X AND
//    is_answered = false", "order by / limit", or "brand_name = X ordered by
//    confirmation_date desc limit 100" the way the old Firestore queries did.
//    Re-fetching guarantees the result the UI sees always matches what a
//    fresh query would return, at the cost of one extra round trip per
//    change event (acceptable given these are low-frequency, per-brand
//    tables, not firehoses).
//
// 3. Tables with a typed `brand_name` column use it for BOTH the Realtime
//    `filter` (server-side broadcast scoping, on top of RLS) and the
//    Postgres RLS ownership check (`daf_confirmation.owns_brand(brand_name)`,
//    see daf_confirmation_rls.sql). Tables WITHOUT a typed `brand_name`
//    column (`non_ai_chat_api`, `bosta_api`) rely on RLS alone (keyed off
//    `data->>'brand'` / `data->>'brand_name'` respectively) — no `filter` is
//    passed to the channel for those, Realtime still only broadcasts rows
//    the connected session's RLS allows it to see.
//    ⚠️ CONFIRMED LIVE (docker exec into dafinv-db, read-only, 2026-07-05):
//    tables that DO write through this module (knowledge_base_api via
//    addKnowledgeItem, non_ai_chat_api via addToNonAiChat) must explicitly
//    set the typed `brand_name` column themselves on INSERT — there is NO
//    generic trigger that derives typed columns from the `data` jsonb for
//    these tables (only `brand_stats_api` has dedicated sync triggers, see
//    `daf inventory/supabase/daf_confirmation_triggers.sql`, itself NOT YET
//    APPLIED to the live stack per that file's own header). If a typed
//    `brand_name` is left null on insert, `owns_brand(null)` returns false
//    (see `daf_confirmation_rls.sql`'s `owns_brand`: `p_key is not null`
//    guard) and the RLS `with check` clause REJECTS the insert outright —
//    this is not a soft failure, it is a hard permission-denied error. Every
//    write function below sets the typed column explicitly for this reason.
//
// 4. ⚠️ ambiguity found and flagged (see also MIGRATION_MAP.md "Ambiguities"):
//    `chat_histories_api.data.last_updated_timestamp` — the field the OLD
//    code orders chat lists by (`orderBy('last_updated_timestamp','desc')`)
//    — is stored in a MIXED FORMAT in the live migrated data (confirmed live,
//    read-only sample against dafinv-db, 2026-07-05): some rows hold an
//    ISO-8601 string (`"2026-07-04T14:45:39.489Z"`), others hold a raw
//    epoch-milliseconds number as JSON (`1780960904239`). PostgREST/
//    supabase-js's `.order('data->>last_updated_timestamp', ...)` would sort
//    these as TEXT, which does NOT produce chronological order across a
//    mixed set (e.g. the string "1780960904239" sorts before "2026-..." as
//    plain text, even though the underlying moments could be in any actual
//    order). The typed `last_updated` timestamptz column on the same table
//    is NOT reliably populated either (confirmed live: frequently NULL on
//    rows that DO have a valid `last_updated_timestamp` in their jsonb).
//    There is no clean, additive-file-only fix for this — the correct fix is
//    a small Postgres function/view doing a `CASE` cast (numeric vs ISO
//    string) server-side, which is new SQL and out of this task's scope
//    (zero new SQL, additive dashboard files only). `subscribeChatHistories`
//    below therefore orders by the typed `last_updated` column (the closest
//    "real timestamp, sortable natively" option) and documents this
//    discrepancy prominently rather than silently producing a subtly wrong
//    order. Flagged for an explicit owner/Wave-4 decision.
//
// 5. ⚠️ `bosta_api` currently holds exactly ONE row in production
//    (`doc_id='base'`, `data={}` — confirmed live, read-only, 2026-07-05).
//    It has NO typed `brand_name`/`timestamp` columns at all (only
//    doc_id/source_collection/data/migrated_at — see
//    daf_confirmation_schema.sql). `subscribeBostaRecords` below is written
//    against the RLS policy's documented "degrades safely" fallback
//    (`data->>'brand_name'`), but since there is no real per-brand data to
//    inspect yet, the exact jsonb key names used for a real Bosta record's
//    "brand" and "timestamp" fields are UNVERIFIED — re-check against a real
//    record (once Bosta auto-sync is actually wired up for a brand) before
//    trusting this path in production.
//
// 6. ⚠️ Realtime `filter` strings below (e.g. `` `brand_name=eq.${brand}` ``)
//    are plain template-string interpolation, not escaped/encoded. Every
//    real brand name observed live today is a simple slug (Alsayes,
//    Cocobelle, Glosso, cleanzy, ...) with no commas/dots/parens — the
//    characters PostgREST's filter grammar treats specially — so this is
//    safe in practice today. It is NOT hardened against a hypothetical
//    future brand name containing those characters (a comma, for instance,
//    would break the filter's parsing since PostgREST/Realtime use commas
//    to separate multiple filter clauses). Flagged rather than silently
//    "handled" — a real fix would need a small escaping helper (or, better,
//    switching to the RLS-only-no-filter pattern used for `non_ai_chat_api`
//    /`bosta_api`/`clients_api` above throughout, at the cost of slightly
//    more Realtime traffic reaching the client before RLS/filtering on the
//    subscriber side discards it).
// ============================================================================

import { supabase } from '../supabaseClient';
import { getMyBrand, updateMyBrandSettings } from './supabaseAuth';

const SCHEMA = 'daf_confirmation';

// ----------------------------------------------------------------------------
// Internal helpers
// ----------------------------------------------------------------------------

let channelSeq = 0;
function nextChannelName(prefix) {
  channelSeq += 1;
  return `${prefix}-${channelSeq}-${Date.now()}`;
}

/**
 * Runs `fetcher()` once immediately, then again on every postgres_changes
 * event on `table` (optionally scoped server-side by `filter`, e.g.
 * `brand_name=eq.Alsayes`). See design note (2) above for why full re-fetch
 * was chosen over payload-patching.
 *
 * @param {string} table
 * @param {string|undefined} filter - PostgREST-style equality filter string,
 *   e.g. `brand_name=eq.${brand}`, or undefined to rely on RLS alone.
 * @param {() => Promise<{rows: any[], error: any}>} fetcher
 * @param {(result: {rows: any[], error: any}) => void} cb
 * @returns {() => void} unsubscribe
 */
/**
 * ⚠️ G11.3.e — THE MOST EXPENSIVE THING THIS APP DOES, and it happens whether
 * anyone is looking or not.
 *
 * Every postgres_changes event re-ran `fetcher()` in full. For a brand taking
 * orders continuously that means re-downloading its whole order list every few
 * seconds — and while a search was open, `fetchFinalOrders` had DROPPED its
 * limit, so each of those refetches pulled the brand's entire history.
 *
 * Two changes, no behaviour lost:
 *   · COALESCE the burst. n8n writes an order row several times in a row as it
 *     moves through the flow (pending → tagged → confirmed), so one customer
 *     reply used to fire three or four full refetches. A trailing window
 *     collapses a burst into one.
 *   · CAP the rate. Even under sustained writes there is at most one refetch
 *     per REFRESH_WINDOW_MS, so the load is bounded by the clock instead of by
 *     how well the brand is selling.
 *
 * Payload-patching (applying the changed row directly) is still deliberately
 * NOT done — design note (2) — because a patched row can't know whether it
 * still belongs in the current sort/filter window. Rate-limiting the refetch
 * keeps that correctness and removes the cost.
 */
const REFRESH_WINDOW_MS = 1500;

function subscribeWithRefetch(table, filter, fetcher, cb) {
  let cancelled = false;
  let timer = null;
  let inFlight = false;
  let pending = false;

  const runFetch = () => {
    if (cancelled) return;
    if (inFlight) {
      // A refetch is already running; remember that it's now stale rather than
      // starting a second one alongside it.
      pending = true;
      return;
    }
    inFlight = true;
    fetcher()
      .then((result) => {
        if (!cancelled) cb(result);
      })
      .catch(() => {
        /* a dropped refresh is recoverable — the next event refetches */
      })
      .finally(() => {
        inFlight = false;
        if (pending && !cancelled) {
          pending = false;
          schedule();
        }
      });
  };

  const schedule = () => {
    if (cancelled || timer) return;
    timer = setTimeout(() => {
      timer = null;
      runFetch();
    }, REFRESH_WINDOW_MS);
  };

  runFetch(); // the first load is immediate — only the reactions are throttled

  const channel = supabase
    .channel(nextChannelName(`cf-${table}`))
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: SCHEMA,
        table,
        ...(filter ? { filter } : {}),
      },
      () => schedule()
    )
    .subscribe();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
    supabase.removeChannel(channel);
  };
}

// ============================================================================
// 1) Identity / "my brand" realtime — replaces ClientDashboard.js's
//    `clientQuery` onSnapshot effect (L3879-3934), `user?.uid` branch only.
//    The `shopifyShop`/`viewingClientUid` (uid-in-URL) branches are
//    intentionally NOT reproduced — see supabaseAuth.js "Known gaps" #1/#2.
// ============================================================================

/**
 * Subscribes to changes on the caller's OWN brand row. Internally re-uses
 * `getMyBrandSettings` (RPC-backed, auth.uid()-scoped — see
 * ./supabaseAuth.js's `getMyBrand`) for both the initial fetch and every
 * refetch-on-change, so there is no brand-name/uid parameter to pass or
 * misuse here at all.
 *
 * @param {(result: {brand: object|null, error: any}) => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeMyBrand(cb) {
  let cancelled = false;
  const runFetch = () => {
    getMyBrand().then((result) => {
      if (!cancelled) cb(result);
    });
  };
  runFetch();

  // No `filter` here on purpose: `auth_uid` lives inside the `data` jsonb,
  // not a top-level column, so a flat `column=eq.value` Realtime filter
  // can't target it directly. RLS (`owns_brand`/`is_super_admin`) still
  // scopes what this session can ever see broadcast from `clients_api`,
  // per design note (3) above.
  const channel = supabase
    .channel(nextChannelName('cf-clients_api-mine'))
    .on(
      'postgres_changes',
      { event: '*', schema: SCHEMA, table: 'clients_api' },
      () => runFetch()
    )
    .subscribe();

  return () => {
    cancelled = true;
    supabase.removeChannel(channel);
  };
}

// ============================================================================
// 2) Orders
// ============================================================================

/**
 * Replaces the "Effect الأوردرات" pair at ClientDashboard.js L1024-1078
 * (`collection(db,'final_orders_api')`, `where('brand_name','==',brand)`,
 * `orderBy('confirmation_date','desc')`, search mode = no limit, normal mode
 * = `limit(100)`). `confirmation_date` is a real typed `timestamptz` column
 * on `final_orders_api` (confirmed live) — safe to order by natively.
 *
 * @param {string} brand
 * @param {{search?: boolean}} params - `search:true` mirrors the old
 *   "isSearching" branch (fetch everything, no limit).
 * @param {(result: {rows: any[], error: any}) => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeFinalOrders(brand, params, cb) {
  // `params.filters` is the G11 shape ({phone, orderNumber, name}) and is
  // pushed into the QUERY. The legacy `params.search` boolean is deliberately
  // ignored now: its only meaning was "drop the limit", which is the bug.
  const { filters } = params || {};
  const fetcher = () => fetchFinalOrders(brand, { filters });
  return subscribeWithRefetch(
    'final_orders_api',
    `brand_name=eq.${brand}`,
    fetcher,
    cb
  );
}

// ============================================================================
// G11 + G12.2 — search and paging, done by the database.
//
// WHAT IT DID BEFORE (and why it was built that way): on Firestore every READ
// cost money, so the cheapest design was to fetch once and filter in the
// browser. `fetchFinalOrders` therefore DROPPED its `.limit(100)` the moment a
// search string existed, pulled the brand's entire history, shipped it to the
// phone, and matched with `String.includes`. On Postgres that reasoning
// inverts: the filter belongs next to the data and the expensive part is
// shipping 15,074 rows to a handset.
//
// Joo: "متوقّعش ولا تحمّل على السيرفر أوي" — so the aim isn't just "make search
// work", it's to make searching CHEAPER than not searching, which it now is:
// a phone lookup is a 0.08ms index hit returning one row, against a 100-row
// page for the unfiltered view.
//
// Three rules:
//   1. the LIMIT is never removed, for any query, ever
//   2. every filter is a WHERE clause, never a .filter() in JS
//   3. the brand scope (`.eq('brand_name')`) is applied first and unconditionally
// ============================================================================

/** Columns the list actually renders. `select('*')` also dragged the whole
 *  `data` jsonb across the wire for every row — by far the biggest field. */
const ORDER_LIST_COLUMNS =
  'doc_id,order_id,customer_phone,brand_name,confirmation_date,final_status,customer_name_lower,data';

// G12.3.a — both confirmation lists and Daf One use a 50-row desktop page.
// This is small enough to keep first paint and mobile memory predictable,
// while the IntersectionObserver gets the next page before the merchant
// reaches the end.
export const ORDERS_PAGE_SIZE = 50;

/** Last 9 digits — the same normalisation Daf One uses, so a phone typed in
 *  either product matches the same rows. Handles "0106 087 1051", "+20106…". */
export function normalizePhoneQuery(input) {
  const digits = String(input || '').replace(/\D/g, '');
  return digits.length >= 9 ? digits.slice(-9) : null;
}

/**
 * The dashboard has SEPARATE boxes for phone and order number (and now name),
 * so filters arrive as named fields rather than one string that has to be
 * guessed at. Every field present is ANDed — that's what a merchant expects
 * from two filled boxes.
 *
 * @typedef {{phone?: string, orderNumber?: string, name?: string}} OrderFilters
 */

/** true when any filter is actually set — replaces the old `isSearching` flag. */
export function hasOrderFilters(f) {
  if (!f) return false;
  return !!(String(f.phone || '').trim() || String(f.orderNumber || '').trim() || String(f.name || '').trim());
}

/**
 * Apply the filters to a PostgREST query builder. Returns the builder so the
 * caller keeps chaining — the whole point is that these reach the DATABASE
 * instead of being re-implemented as `String.includes` in the browser.
 */
function applyOrderSearch(q, filters) {
  if (!filters) return q;
  const phone = String(filters.phone || '').trim();
  const orderNumber = String(filters.orderNumber || '').trim();
  const name = String(filters.name || '').trim();

  if (phone) {
    // G11.2.c — the stored value is "201060871051" and the merchant types
    // "01060871051" or "0106 087 1051". Normalising BOTH to the last 9 digits
    // is what makes the search work at all; it never did before.
    const last9 = normalizePhoneQuery(phone);
    q = last9
      ? q.eq('phone_last9', last9)
      : // fewer than 9 digits typed — a partial, so a contains match on the
        // normalised column rather than matching nothing
        q.ilike('phone_last9', `%${phone.replace(/\D/g, '')}%`);
  }
  if (orderNumber) {
    // 🔥 (2026-08-20) كان بيدوّر في `order_id` (الرقم الداخلي الطويل بتاع
    // شوبيفاي، زي 8298825973847) — ده مش رقم الأوردر اللي التاجر شايفه على
    // الشاشة (زي 13062)، ده متخزّن في `data->>'order_number'`. أول تصليح
    // ضاف OR على order_id "للتوافق" — لكن ده رجّع false positives فعلية
    // (order_id طويل بيحتوي بالصدفة على تتابع الأرقام المطلوب، زي
    // "7589130829911" اللي فيه "13082" بالصدفة). order_id مالوش أي نظير
    // بالرقم اللي التاجر شايفه فعلاً، فمفيش داعي له خالص — البحث بقى في
    // `data->>'order_number'` بس.
    const esc = orderNumber.replace(/\s/g, '');
    q = q.ilike('data->>order_number', `%${esc}%`);
  }
  if (name) {
    // G11.2.b — Joo asked for name search; the dashboard never had one. The
    // name lives inside `data` with no column, so migration 001 generates a
    // lower-cased one that an index can actually use.
    q = q.ilike('customer_name_lower', `%${name.toLowerCase()}%`);
  }
  return q;
}

/**
 * One page of orders, filtered and paged in the database.
 *
 * ⚠️ G12.2.d — KEYSET, and it must be a TOTAL order. `confirmation_date` is
 * NULL on 4 live rows and the sync trigger has produced nulls before, so the
 * cursor carries `doc_id` as a tiebreak and the null-dated tail is paged by
 * doc_id alone. Without that a page boundary silently skips or repeats rows.
 */
export async function fetchOrdersPage(brand, { filters, cursor, pageSize = ORDERS_PAGE_SIZE } = {}) {
  let q = supabase
    .from('final_orders_api')
    .select(ORDER_LIST_COLUMNS)
    .eq('brand_name', brand)
    .order('confirmation_date', { ascending: false, nullsFirst: false })
    .order('doc_id', { ascending: false })
    .limit(pageSize + 1); // +1 → "is there more" is a fact, not a guess

  q = applyOrderSearch(q, filters);

  if (cursor) {
    if (cursor.date) {
      // Rows strictly older than the cursor, plus the same-timestamp rows whose
      // doc_id sorts after it. PostgREST has no row-value comparison, so it's
      // spelled out as an OR.
      q = q.or(
        `confirmation_date.lt.${cursor.date},and(confirmation_date.eq.${cursor.date},doc_id.lt.${cursor.id})`
      );
    } else {
      // We're already in the NULL-dated tail: order by doc_id alone.
      q = q.is('confirmation_date', null).lt('doc_id', cursor.id);
    }
  }

  const { data, error } = await q;
  const rows = data || [];
  const hasMore = rows.length > pageSize;
  const page = hasMore ? rows.slice(0, pageSize) : rows;
  const last = page[page.length - 1];
  return {
    rows: page,
    error,
    nextCursor: hasMore && last ? { date: last.confirmation_date || null, id: last.doc_id } : null,
  };
}

/**
 * The TOTAL for this brand + search, from the database.
 * `head: true` means Postgres counts and returns NO rows at all — the count
 * used to be "however many happen to be loaded", which shrank as soon as
 * paging existed.
 */
export async function countOrders(brand, { filters } = {}) {
  let q = supabase
    .from('final_orders_api')
    .select('doc_id', { count: 'exact', head: true })
    .eq('brand_name', brand);
  q = applyOrderSearch(q, filters);
  const { count, error } = await q;
  return { count: count ?? 0, error };
}

export async function fetchFinalOrders(brand, { filters } = {}) {
  // 🔥 (2026-07-13) `nullsFirst: false` is a defensive belt-and-suspenders
  // addition, NOT the fix for the "Today/Yesterday show 0 orders" bug — the
  // real root cause was `final_orders_api.confirmation_date` (the typed
  // column this `.order()` sorts by) being left NULL by the sync trigger for
  // every order written since the 2026-07-06 Postgres cutover (see
  // `daf_confirmation_sync_typed_columns.sql`'s 2026-07-13 update, which
  // fixes/backfills that column at the source). Without this option,
  // Postgres's default null-ordering for `ascending:false` is NULLS FIRST,
  // so if the typed column is ever NULL again for some future row (e.g. a
  // write path that omits `confirmation_date` from `data` entirely — a few
  // such rows already exist), it would silently jump to the very top of the
  // `.limit(100)` window below and push a real recent order off page 1,
  // exactly like the bug this fixes. With `nullsFirst:false`, any
  // NULL-dated row instead sorts to the *bottom* (harmless: it just won't
  // appear on page 1 unless the merchant searches, which already bypasses
  // the limit).
  //
  // ⚠️ G11.2.a — `if (!search) q = q.limit(100)` used to live here, i.e. the
  // limit was REMOVED as soon as the merchant typed a character. That made the
  // search the single heaviest operation in the product: every keystroke pulled
  // the brand's whole history (15,074 rows across 33 brands; a big brand is
  // thousands on its own), shipped it to the browser, and matched it there with
  // String.includes — and `subscribeWithRefetch` then repeated the whole thing
  // on every realtime event for as long as the box stayed open.
  //
  // The limit now applies unconditionally. Searching is a WHERE clause
  // (fetchOrdersPage/applyOrderSearch above), so it makes the query cheaper
  // rather than unbounded.
  let q = supabase
    .from('final_orders_api')
    .select(ORDER_LIST_COLUMNS)
    .eq('brand_name', brand)
    .order('confirmation_date', { ascending: false, nullsFirst: false })
    .order('doc_id', { ascending: false })
    .limit(ORDERS_PAGE_SIZE);
  q = applyOrderSearch(q, filters);
  const { data, error } = await q;
  return { rows: data || [], error };
}

/**
 * Offset-based "load more" — replaces `handleLoadMoreChats`-style cursor
 * pagination (`startAfter(lastVisibleOrder)`) at ClientDashboard.js. Uses
 * `.range()` instead of a Firestore document cursor.
 * ⚠️ Not perfectly equivalent under concurrent inserts (offset pagination
 * can skip/repeat a row if new rows are inserted between pages — Firestore's
 * cursor-based `startAfter` does not have this issue). Acceptable trade-off
 * for an additive, non-cutover-yet layer; flagged in MIGRATION_MAP.md.
 */
export async function fetchMoreFinalOrders(brand, offset, pageSize = 100) {
  // ⚠️ G11.3.b — OFFSET paging, kept ONLY so any caller that hasn't moved to
  // fetchOrdersPage still works. It has two problems the keyset version doesn't:
  // it gets slower the deeper you go (Postgres walks and discards every skipped
  // row), and under concurrent inserts — which this table has constantly, from
  // n8n — a row can be skipped or repeated across a page boundary.
  //
  // New callers use fetchOrdersPage(). This is deprecated, not load-bearing.
  const { data, error } = await supabase
    .from('final_orders_api')
    .select(ORDER_LIST_COLUMNS)
    .eq('brand_name', brand)
    .order('confirmation_date', { ascending: false, nullsFirst: false })
    .order('doc_id', { ascending: false })
    .range(offset, offset + pageSize - 1);
  return { rows: data || [], error };
}

/**
 * Replaces the "Effect للـ Pending Orders" at ClientDashboard.js L1191-1212
 * (`where('brand_name','==',brand)`, no order/limit — always fetches all of
 * a brand's pending orders).
 */
export function subscribePendingOrders(brand, params, cb) {
  const { phoneSearch, orderSearch } = params || {};
  const fetcher = () => fetchPendingOrders(brand, { phoneSearch, orderSearch });
  return subscribeWithRefetch(
    'pending_orders_api',
    `brand_name=eq.${brand}`,
    fetcher,
    cb
  );
}

export async function fetchPendingOrders(brand, { phoneSearch, orderSearch } = {}) {
  let q = supabase.from('pending_orders_api').select('*').eq('brand_name', brand);
  if (phoneSearch && phoneSearch.trim()) {
    const digits = phoneSearch.trim().replace(/\D/g, '');
    if (digits.length > 0) q = q.ilike('customer_phone', `%${digits}%`);
  }
  if (orderSearch && orderSearch.trim()) {
    q = q.ilike('data->>order_number', `%${orderSearch.trim()}%`);
  }
  const { data, error } = await q;
  return { rows: data || [], error };
}

// ============================================================================
// 3) Chats
// ============================================================================

/**
 * Replaces the "(جديد) Effect المحادثات" pair at ClientDashboard.js
 * L1242-1313 (`collection(db,'chat_histories_api')`,
 * `where('brand_name','==',brand)`, `orderBy('last_updated_timestamp',
 * 'desc')`, search mode = no limit, normal mode = `limit(100)`).
 *
 * ⚠️ See design note (4) above: this orders by the typed `last_updated`
 * column (NOT `data->>'last_updated_timestamp'`, which is mixed-format live
 * data and cannot be ordered correctly through PostgREST's plain
 * text-ordering of a JSON path). `last_updated` is frequently NULL in the
 * live data today, so ordering will differ from the old Firestore behavior
 * until that column is populated consistently (flagged, not silently
 * "fixed" here — see MIGRATION_MAP.md "Ambiguities").
 */
export function subscribeChatHistories(brand, params, cb) {
  const { search, phoneSearch } = params || {};
  const fetcher = () => fetchChatHistories(brand, { search, phoneSearch });
  return subscribeWithRefetch(
    'chat_histories_api',
    `brand_name=eq.${brand}`,
    fetcher,
    cb
  );
}

export async function fetchChatHistories(brand, { search, phoneSearch } = {}) {
  let q = supabase
    .from('chat_histories_api')
    .select('*')
    .eq('brand_name', brand)
    .order('last_updated', { ascending: false, nullsFirst: false });

  if (phoneSearch && phoneSearch.trim()) {
    // server-side partial match — strip non-digits then ilike
    const digits = phoneSearch.trim().replace(/\D/g, '');
    if (digits.length > 0) {
      q = q.ilike('customer_phone', `%${digits}%`);
    }
    // no limit when searching — server handles it
  } else {
    q = q.limit(ORDERS_PAGE_SIZE);
  }

  const { data, error } = await q;
  return { rows: data || [], error };
}

export async function fetchMoreChatHistories(brand, offset, pageSize = ORDERS_PAGE_SIZE) {
  const { data, error } = await supabase
    .from('chat_histories_api')
    .select('*')
    .eq('brand_name', brand)
    .order('last_updated', { ascending: false, nullsFirst: false })
    .range(offset, offset + pageSize - 1);
  return { rows: data || [], error };
}

/**
 * Replaces the THREE identical single-doc chat listeners at
 * ClientDashboard.js:
 *   - L1904-1962 (`handleOpenFeedbackModal`)
 *   - L2032-2077 (`handleOpenBostaModal`)
 *   - L2139-2200 (`handleOpenChatModal`, the pending-orders/leads modal)
 * All three do the exact same thing: `doc(db,'chat_histories_api',
 * `${brand}_${phone}`)` + `onSnapshot`, sort `data().messages` client-side by
 * a `getSafeTime(timestamp)` helper that tolerates Firestore Timestamp /
 * Date / number shapes. This single function replaces all three call sites.
 *
 * Unlike the list-style subscriptions above, this uses a precise
 * `doc_id=eq.<id>` Realtime filter (single row, cheap and exact) and DOES
 * still re-fetch on change (rather than trusting the raw payload) for
 * consistency with every other function in this file and because
 * `chat_histories_api` is not in the "typed brand_name" special-case list —
 * see design note (2).
 *
 * @param {string} brand
 * @param {string} phone - `customer_phone` from the order/feedback/bosta row
 *   that was clicked, exactly as the old code builds `docId` (L1904, L2032,
 *   L2139: `` `${clientInfo.brand_name}_${feedback.customer_phone}` ``).
 * @param {(result: {messages: any[], error: any}) => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeChatThread(brand, phone, cb) {
  const docId = `${brand}_${phone}`;
  const fetcher = () => fetchChatThread(docId);
  return subscribeWithRefetch(
    'chat_histories_api',
    `doc_id=eq.${docId}`,
    fetcher,
    cb
  );
}

function getSafeTime(timestamp) {
  if (!timestamp) return 0;
  if (typeof timestamp === 'number') return timestamp;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

export async function fetchChatThread(docId) {
  const { data, error } = await supabase
    .from('chat_histories_api')
    .select('data')
    .eq('doc_id', docId)
    .maybeSingle();
  if (error) return { messages: [], error };
  const messages = (data && data.data && data.data.messages) || [];
  // Same client-side chronological sort as the old code (messages are
  // stored newest-appended-last inside the jsonb array, but the old code
  // re-sorts defensively — replicated verbatim).
  const sorted = [...messages].sort(
    (a, b) => getSafeTime(a.timestamp) - getSafeTime(b.timestamp)
  );
  return { messages: sorted, error: null };
}

/**
 * Replaces the "Effect لجلب non-AI chats" at ClientDashboard.js L1214-1240
 * (`collection(db,'non-ai-chat_api')` — ⚠️ NOTE the Firestore collection
 * name uses HYPHENS, the migrated Postgres TABLE name uses UNDERSCORES:
 * `non_ai_chat_api` — this exact naming trap is called out in this
 * project's own context doc, DAF_CONFIRMATION_CONTEXT.md, "اكتشافان مهمّان"
 * entry — using the hyphenated name here would be a straight SQL identifier
 * error, not a silent bug, so it's an easy one to catch at review time, but
 * flagged anyway since it bit a previous n8n-conversion task).
 * `where('brand', '==', brand)` — note the FIELD name inside `data` is
 * `brand`, not `brand_name` (different from every other table in this
 * file) — confirmed live: `non_ai_chat_api` has no typed brand column at
 * all, only `data->>'brand'` (matches `daf_confirmation_rls.sql`'s policy
 * for this table exactly).
 */
export function subscribeNonAiChats(brand, cb) {
  const fetcher = () => fetchNonAiChats(brand);
  // No `filter` param: `brand` lives inside `data` (jsonb), not a typed
  // column, so there is no flat column to filter Realtime on server-side —
  // RLS (`owns_brand(data->>'brand')`) still scopes what's ever broadcast.
  return subscribeWithRefetch('non_ai_chat_api', undefined, fetcher, cb);
}

export async function fetchNonAiChats(brand) {
  const { data, error } = await supabase
    .from('non_ai_chat_api')
    .select('*')
    .eq('data->>brand', brand);
  return { rows: data || [], error };
}

/**
 * Replaces the INSERT branch of `handleToggleNonAiChat`
 * (ClientDashboard.js L2263-2300, "إضافة لـ non-AI" — the old code's
 * `setDoc(chatDocRef, {...})`). Sets `data.brand` (the field this table's
 * RLS policy keys on — see comment on `subscribeNonAiChats` above) so the
 * insert passes the `with check` clause.
 *
 * @param {string} brand
 * @param {string} phone
 * @param {string} platform
 */
export async function addToNonAiChat(brand, phone, platform) {
  const docId = `${brand}_${phone}`;
  const { error } = await supabase.from('non_ai_chat_api').upsert({
    doc_id: docId,
    data: {
      phoneNumber: phone,
      brand,
      platform: platform || 'unknown',
      addedAt: new Date().toISOString(),
      unreadCount: 0,
    },
  });
  return { error };
}

/**
 * ⚠️ BLOCKED under the live Wave 3 grants — replaces the DELETE branch of
 * `handleToggleNonAiChat` (ClientDashboard.js L2279-2282, "حذف من non-AI
 * (رجوع للـ AI)": `await deleteDoc(chatDocRef)`).
 * `daf_confirmation_rls.sql` grants `authenticated` only
 * `select, insert, update` on every one of the 9 non-secret tables — there
 * is NO `delete` grant anywhere in that file ("No DELETE grant to
 * authenticated anywhere (only service_role keeps it)"). Calling this today
 * (once PostgREST exposure + GoTrue provisioning are done) will fail with a
 * Postgres permission-denied error, not a soft/empty result. See
 * MIGRATION_MAP.md, "Ambiguity #1 — missing DELETE grant" for the 3 affected
 * call sites (this one, `deleteQuestion`, `deleteKnowledgeItem`) and the two
 * remediation options (extend the GRANT, or route through a service-role
 * server endpoint) — deciding between them is out of this task's scope
 * (zero new SQL).
 */
export async function removeFromNonAiChat(docId) {
  const { error } = await supabase
    .from('non_ai_chat_api')
    .delete()
    .eq('doc_id', docId);
  return { error };
}

/**
 * Replaces the `updateDoc(nonAiChatDocRef, {unreadCount: 0})` calls at
 * ClientDashboard.js L1908-1919, L2035-2040, L2143-2151 (all three "open
 * chat modal" handlers reset the unread counter the same way).
 */
export async function resetChatUnreadCount(brand, phone) {
  const docId = `${brand}_${phone}`;
  // Firestore's `updateDoc({unreadCount: 0})` merged ONLY that key into the
  // doc. A plain Postgres `update ... set data = {...}` REPLACES the whole
  // `data` jsonb, wiping phoneNumber/brand/platform/addedAt. So we read-
  // modify-write (fetch current data, spread, set unreadCount) — the same
  // merge pattern as `updateKnowledgeItem` below. The row may not exist yet
  // (the unread-reset fires on modal open even for chats never added to the
  // non-AI list); in that case there's nothing to reset, so we no-op rather
  // than create a bogus row. (Small read-then-write race is acceptable — it
  // matches the old getDoc→updateDoc flow, and the only field in flight is a
  // display counter.)
  const { data: current, error: fetchError } = await supabase
    .from('non_ai_chat_api')
    .select('data')
    .eq('doc_id', docId)
    .maybeSingle();
  if (fetchError) return { error: fetchError };
  if (!current) return { error: null };

  const nextData = { ...(current.data || {}), unreadCount: 0 };
  const { error } = await supabase
    .from('non_ai_chat_api')
    .update({ data: nextData })
    .eq('doc_id', docId);
  return { error };
}

// ============================================================================
// 4) Feedbacks
// ============================================================================

/**
 * Replaces the "Effect الـ Feedbacks" pair at ClientDashboard.js L894-959
 * (`where('brand_name','==',brand)`, `orderBy('timestamp','desc')`, search
 * mode = no limit, normal mode = `limit(100)`). `timestamp` is a
 * consistently-ISO-8601-string jsonb field (confirmed live) — safe to order
 * by as `data->>timestamp` (text ordering of ISO-8601 strings is
 * chronological, unlike the mixed-format `last_updated_timestamp` case on
 * `chat_histories_api` — see design note 4 above).
 */
export function subscribeFeedbacks(brand, params, cb) {
  const { search, phoneSearch } = params || {};
  const fetcher = () => fetchFeedbacks(brand, { search, phoneSearch });
  return subscribeWithRefetch(
    'feedbacks_api',
    `brand_name=eq.${brand}`,
    fetcher,
    cb
  );
}

export async function fetchFeedbacks(brand, { search, phoneSearch } = {}) {
  let q = supabase
    .from('feedbacks_api')
    .select('*')
    .eq('brand_name', brand)
    .order('data->>timestamp', { ascending: false });
  if (phoneSearch && phoneSearch.trim()) {
    const digits = phoneSearch.trim().replace(/\D/g, '');
    if (digits.length > 0) q = q.ilike('customer_phone', `%${digits}%`);
    // no limit when searching — server handles it
  } else {
    q = q.limit(ORDERS_PAGE_SIZE);
  }
  const { data, error } = await q;
  return { rows: data || [], error };
}

export async function fetchMoreFeedbacks(brand, offset, pageSize = ORDERS_PAGE_SIZE) {
  const { data, error } = await supabase
    .from('feedbacks_api')
    .select('*')
    .eq('brand_name', brand)
    .order('data->>timestamp', { ascending: false })
    .range(offset, offset + pageSize - 1);
  return { rows: data || [], error };
}

// ============================================================================
// 5) Bosta records
// ============================================================================

/**
 * Replaces the "Effect الـ Bosta" pair at ClientDashboard.js L961-1022
 * (`where('brand_name','==',brand)`, `orderBy('timestamp','desc')`, search
 * mode = no limit, normal mode = `limit(100)`).
 *
 * ⚠️ See design note (5) above: `bosta_api` currently holds exactly one
 * placeholder row live (`doc_id='base'`) — there is no real per-brand
 * record to confirm the jsonb field names against yet. This is written to
 * match `daf_confirmation_rls.sql`'s documented fallback
 * (`data->>'brand_name'`) but is UNVERIFIED against real data.
 */
export function subscribeBostaRecords(brand, params, cb) {
  const { search, phoneSearch } = params || {};
  const fetcher = () => fetchBostaRecords(brand, { search, phoneSearch });
  // No typed `brand_name` column on this table (confirmed live) — no
  // server-side Realtime `filter` possible, RLS still scopes visibility.
  return subscribeWithRefetch('bosta_api', undefined, fetcher, cb);
}

export async function fetchBostaRecords(brand, { search, phoneSearch } = {}) {
  let q = supabase
    .from('bosta_api')
    .select('*')
    .eq('data->>brand_name', brand)
    .order('data->>timestamp', { ascending: false });
  if (phoneSearch && phoneSearch.trim()) {
    const digits = phoneSearch.trim().replace(/\D/g, '');
    if (digits.length > 0) q = q.ilike('data->>customer_phone', `%${digits}%`);
    // no limit when searching — server handles it
  } else {
    q = q.limit(ORDERS_PAGE_SIZE);
  }
  const { data, error } = await q;
  return { rows: data || [], error };
}

export async function fetchMoreBostaRecords(brand, offset, pageSize = ORDERS_PAGE_SIZE) {
  const { data, error } = await supabase
    .from('bosta_api')
    .select('*')
    .eq('data->>brand_name', brand)
    .order('data->>timestamp', { ascending: false })
    .range(offset, offset + pageSize - 1);
  return { rows: data || [], error };
}

// ============================================================================
// 6) Brand stats (single doc, doc_id === brand name)
// ============================================================================

/**
 * Replaces the "Effect لقراءة إحصائيات الأوردرات" at ClientDashboard.js
 * L1080-1142 (`doc(db,'brand_stats_api', brand)` + `onSnapshot`). The old
 * code has a client-side fallback that recomputes stats from `clientOrders`
 * if the stats doc doesn't exist yet — that fallback is a UI-layer concern
 * (depends on already-loaded order state) and is intentionally NOT
 * reproduced inside this data-layer function; the caller should keep doing
 * that fallback the same way once this is wired in (documented in
 * MIGRATION_MAP.md rather than silently duplicated here).
 */
export function subscribeBrandStats(brand, cb) {
  const fetcher = () => fetchBrandStats(brand);
  return subscribeWithRefetch(
    'brand_stats_api',
    `doc_id=eq.${brand}`,
    fetcher,
    cb
  );
}

export async function fetchBrandStats(brand) {
  const { data, error } = await supabase
    .from('brand_stats_api')
    .select('data')
    .eq('doc_id', brand)
    .maybeSingle();
  if (error) return { stats: null, error };
  return { stats: (data && data.data) || null, error: null };
}

// ============================================================================
// 7) Open customer questions
// ============================================================================

/**
 * Replaces the "Effect الأسئلة" at ClientDashboard.js L1144-1166
 * (`where('brand_name','==',brand)`, `where('is_answered','==',false)`,
 * `orderBy('date_added','desc')`). `date_added` is a consistently
 * ISO-8601-string jsonb field on this table (confirmed live) — same
 * text-ordering-is-chronological reasoning as `feedbacks_api.timestamp`
 * above. `is_answered` is a real jsonb boolean (confirmed live, unquoted
 * `false` in `data`) — `.eq('data->>is_answered', 'false')` textifies it via
 * PostgREST's `->>` operator, matching Firestore's `== false` filter.
 */
export function subscribeOpenQuestions(brand, cb) {
  const fetcher = () => fetchOpenQuestions(brand);
  return subscribeWithRefetch(
    'brand_questions_api',
    `brand_name=eq.${brand}`,
    fetcher,
    cb
  );
}

export async function fetchOpenQuestions(brand) {
  const { data, error } = await supabase
    .from('brand_questions_api')
    .select('*')
    .eq('brand_name', brand)
    .eq('data->>is_answered', 'false')
    .order('data->>date_added', { ascending: false });
  return { rows: data || [], error };
}

/**
 * ⚠️ BLOCKED under the live Wave 3 grants — replaces `handleDeleteQuestion`
 * (ClientDashboard.js L1371-1384, `deleteDoc(questionRef)`). Same missing-
 * DELETE-grant issue as `removeFromNonAiChat` above — see MIGRATION_MAP.md,
 * "Ambiguity #1".
 */
export async function deleteQuestion(questionId) {
  const { error } = await supabase
    .from('brand_questions_api')
    .delete()
    .eq('doc_id', questionId);
  return { error };
}

// ============================================================================
// 8) Knowledge base (shared by ClientDashboard.js AND KnowledgeBasePage.js —
//    both pages query the exact same shape today, see MIGRATION_MAP.md)
// ============================================================================

/**
 * Replaces:
 *   - ClientDashboard.js's "Effect قاعدة المعرفة" (L1168-1189)
 *   - KnowledgeBasePage.js's `fetchKnowledgeBase` (L83-110)
 * Both: `where('brand_name','==',brand)`, `orderBy('date_added','desc')`,
 * no limit. Same ISO-8601-string ordering reasoning as `fetchOpenQuestions`
 * above (confirmed live).
 *
 * ⚠️ Note: KnowledgeBasePage.js resolves `brand` today via
 * `where('brand_owner','==',uid)` (L64) — a DIFFERENT field name than every
 * other page (`auth_uid`). Per the task brief, this new layer standardizes
 * on `auth_uid`-via-`getMyBrand()` everywhere — `brand_owner` is not read or
 * written anywhere in this file, on purpose. Any future KnowledgeBasePage.js
 * rewrite should resolve its brand the same way ClientDashboard.js does
 * (via `getMyBrand()`), not via the legacy `brand_owner` field.
 */
export function subscribeKnowledgeBase(brand, cb) {
  const fetcher = () => fetchKnowledgeBase(brand);
  return subscribeWithRefetch(
    'knowledge_base_api',
    `brand_name=eq.${brand}`,
    fetcher,
    cb
  );
}

export async function fetchKnowledgeBase(brand) {
  const { data, error } = await supabase
    .from('knowledge_base_api')
    .select('*')
    .eq('brand_name', brand)
    .order('data->>date_added', { ascending: false });
  return { rows: data || [], error };
}

/**
 * Replaces `handleSubmitNew` (ClientDashboard.js L1452-1474) and
 * KnowledgeBasePage.js's `handleAddKnowledge` (L127-158) — both do
 * `addDoc(collection(db,'knowledge_base_api'), {...})`. Sets the typed
 * `brand_name` column explicitly (see design note 3) so the RLS `with
 * check` clause passes.
 */
export async function addKnowledgeItem(brand, { question, answer, productContext }) {
  const { error } = await supabase.from('knowledge_base_api').insert({
    doc_id:
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `kb_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    brand_name: brand,
    data: {
      brand_name: brand,
      question_text: (question || '').trim(),
      answer_text: (answer || '').trim(),
      product_context: (productContext || '').trim() || '',
      date_added: new Date().toISOString(),
    },
  });
  return { error };
}

/**
 * Replaces `handleSubmitEdit` (ClientDashboard.js L1401-1419) and
 * KnowledgeBasePage.js's `handleSaveEdit` (L186-206) — both
 * `updateDoc(itemRef, {question_text, answer_text, product_context})`.
 *
 * ⚠️ Same jsonb-replace-vs-merge caveat as `resetChatUnreadCount` above: a
 * plain `.update({data: {...}})` REPLACES the whole `data` value. Since
 * this function always supplies question/answer/product_context together
 * (matching what the old code always sent as a full triple, never a
 * partial patch), it happens to be safe here — but `date_added` (not part
 * of this update in the old code either) WOULD be wiped by a naive
 * `data: {...}` replace unless it's carried forward. This function fetches
 * the current row first and spreads it, to replicate Firestore's per-field
 * `updateDoc` merge semantics exactly (unlike `resetChatUnreadCount`, which
 * is flagged instead of "fixed" because it's a hot, high-frequency path —
 * this one is a low-frequency admin edit, so the extra round trip is a
 * non-issue).
 */
export async function updateKnowledgeItem(docId, { question, answer, productContext }) {
  const { data: current, error: fetchError } = await supabase
    .from('knowledge_base_api')
    .select('data')
    .eq('doc_id', docId)
    .maybeSingle();
  if (fetchError) return { error: fetchError };

  const nextData = {
    ...(current && current.data ? current.data : {}),
    question_text: (question || '').trim(),
    answer_text: (answer || '').trim(),
    product_context: (productContext || '').trim() || '',
  };

  const { error } = await supabase
    .from('knowledge_base_api')
    .update({ data: nextData })
    .eq('doc_id', docId);
  return { error };
}

/**
 * ⚠️ BLOCKED under the live Wave 3 grants — replaces
 * `handleDeleteKnowledgeItem` (ClientDashboard.js L1421-1434) and
 * KnowledgeBasePage.js's `handleDeleteKnowledge` (L161-173) — both
 * `deleteDoc(itemRef)`. Same missing-DELETE-grant issue as
 * `removeFromNonAiChat`/`deleteQuestion` — see MIGRATION_MAP.md,
 * "Ambiguity #1".
 */
export async function deleteKnowledgeItem(docId) {
  const { error } = await supabase
    .from('knowledge_base_api')
    .delete()
    .eq('doc_id', docId);
  return { error };
}

// ============================================================================
// 9) Brand settings toggles — thin, named wrappers over
//    `updateMyBrandSettings()` (./supabaseAuth.js), one per handler in
//    ClientDashboard.js, so the eventual wiring pass is a near-mechanical
//    find/replace. All of these go through the SAME whitelisted RPC — see
//    updateMyBrandSettings's own doc comment for the exhaustive whitelist
//    and the `ai_mode`/`order_cost` gap.
// ============================================================================

/** Replaces `handleToggleAutoCancel` (ClientDashboard.js L1519-1551). */
export function setAutoCancel(enabled) {
  return updateMyBrandSettings({ auto_cancel: enabled });
}

/** Replaces `handleToggleDuplicatedOrderNote` (L1554-1585). */
export function setDuplicatedOrderNote(enabled) {
  return updateMyBrandSettings({ dublicated_order_note: enabled });
}

/** Replaces `handleCancelTimeoutChange` (L1588-1612). */
export function setCancelTimeoutHours(hours) {
  return updateMyBrandSettings({ cancel_timeout_hours: hours });
}

/** Replaces `handleFirstMsgDelayChange` (L1629-1654). */
export function setFirstMessageDelayHours(hours) {
  return updateMyBrandSettings({ first_message_delay_hours: hours });
}

/** Replaces `handleToggleDeliveryDate` (L1657-1688). */
export function setDeliveryDateEnabled(enabled) {
  return updateMyBrandSettings({ delivery_date_enabled: enabled });
}

/** Replaces `handleDeliveryDaysChange` (L1691-1716). */
export function setDeliveryAverageDays(days) {
  return updateMyBrandSettings({ delivery_average_days: days });
}

/** Replaces `handleToggleFeedback` (L1719-1751). */
export function setFeedbackEnabled(enabled) {
  return updateMyBrandSettings({ feedback: enabled });
}

/**
 * Replaces `handleToggleAiMode` (L1754-1813). ⚠️ See the detailed gap in
 * `updateMyBrandSettings`'s own doc comment (./supabaseAuth.js): the old
 * handler also flips `order_cost` in the same write — that field is NOT
 * writable through this RPC (billing-relevant typed column, deliberately
 * excluded). This function only sets `ai_mode` (+ `shipping_check: false`
 * when disabling, matching the old handler's forced side effect at
 * L1793-1797 — `shipping_check` IS in the whitelist).
 */
export function setAiMode(enabled) {
  return updateMyBrandSettings(
    enabled ? { ai_mode: true } : { ai_mode: false, shipping_check: false }
  );
}

/** Replaces `handleToggleShippingCheck` (L1816-1849). */
export function setShippingCheckEnabled(enabled) {
  return updateMyBrandSettings({ shipping_check: enabled });
}

/** Replaces `handleToggleBostaActions` (L1852-1884). */
export function setBostaAwaitingActions(enabled) {
  return updateMyBrandSettings({ bosta_awaiting_actions: enabled });
}

/**
 * Replaces `handleSaveInfo` (L607-638) — telegram id / facebook / instagram
 * / message language. Matches the old handler's behavior of always writing
 * all four fields (even empty strings, so a merchant can clear a value) —
 * `updateMyBrandSettings`'s RPC does a jsonb merge (`data || v_filtered`,
 * see daf_confirmation_rls.sql), so passing an empty string genuinely
 * clears the field rather than leaving the previous value, matching the
 * old Firestore `updateDoc` semantics exactly.
 */
export function saveClientContactInfo({ telegram, facebook, instagram, messageLanguage }) {
  return updateMyBrandSettings({
    brand_owner_telegram_ID: String(telegram || '').trim(),
    facebook_url: String(facebook || '').trim(),
    instegram_url: String(instagram || '').trim(), // sic — matches the
    // live field name (`instegram_url`, not `instagram_url`) already used
    // throughout the Firestore-era code (see ClientDashboard.js L620,
    // L1759/L1769, and the RPC whitelist itself in daf_confirmation_rls.sql).
    message_language: messageLanguage,
  });
}

/**
 * 🏗️ 2026-07-07 addition (Daf One → Daf Confirmation auto-provision, frontend
 * wave) — marks `operational_setup_done: true` on the caller's own brand.
 * Whitelisted in `update_my_brand_settings()` by
 * `daf_confirmation_operational_setup.sql` (additive migration, applied
 * live). Used by ClientDashboard.js's "finish setting up" banner/modal after
 * it has already saved the merchant's Telegram id (via
 * `saveClientContactInfo` above) — a SEPARATE RPC call rather than bundling
 * this flag into that same call, so this function stays reusable
 * independently of the contact-info fields (e.g. if a future flow ever needs
 * to just flip this flag without touching telegram/facebook/instagram at all).
 */
export function setOperationalSetupDone() {
  return updateMyBrandSettings({ operational_setup_done: true });
}

// ============================================================================
// See src/lib/MIGRATION_MAP.md for:
//   - the exhaustive, line-numbered mapping of all 18 old onSnapshot
//     listeners + every direct getDoc/getDocs/updateDoc/addDoc/deleteDoc/
//     setDoc call to the functions in this file,
//   - the 5-Cloud-Function-callable -> Express-route migration plan,
//   - the full list of open ambiguities/gaps this file intentionally does
//     NOT paper over (missing DELETE grants, jsonb merge-vs-replace,
//     mixed-format timestamp field, empty bosta_api, super-admin "view as",
//     Shopify-domain auto-login, ai_mode/order_cost coupling),
//   - the required env vars.
// ============================================================================
